import { camelizeKeys } from 'humps'

import { IS_WEB, SITE_URL } from '../config'

import { parseUrl } from './url'

const WP_CATEGORY_ID_PRESS = 3
const WP_CATEGORY_ID_BLOGPOST = 8
const WP_CATEGORY_ID_AI_GLOSSARY = 18

const WP_API_URL = 'https://wp2.snips.ai/wp-json/wp/v2/'

const WHITELIST_PAGE_FIELDS = [
  'id',
  'acf',
  'link',
  'slug',
  'title',
  'yoast_meta'
]
const dropFields = (object, fieldsWhitelist) => {
  return fieldsWhitelist.reduce((formatted, field) => {
    formatted[field] = object[field]
    return formatted
  }, {})
}

function execWPQuery (apiQueryUrl) {
  return window
    .fetch(apiQueryUrl)
    .then(res => {
      if (res.status !== 200) {
        throw new Error('got bad response from WP api: ' + res.statusText)
      }
      return res.json()
    })
    .then(data => {
      const camelized = camelizeKeys(data)
      // TODO: drop keys too?
      return camelized
    })
}

// NOTE: we're just using slug as a way to find one specific
export function getWpPostBySlug (slug) {
  const cacheBuster = Date.now()
  // TODO: add the category filter too?
  const apiQueryUrl = WP_API_URL + `posts/?${cacheBuster}&slug=${slug}`
  return execWPQuery(apiQueryUrl).then(data => data && data[0])
}
export function getWpPost (wpPostId) {
  const cacheBuster = Date.now()
  const apiQueryUrl = WP_API_URL + `posts/${wpPostId}?${cacheBuster}`
  return execWPQuery(apiQueryUrl)
}

export function getWpPosts ({ category, search }) {
  const cacheBuster = Date.now()
  let apiQueryUrl = WP_API_URL + `posts/?${cacheBuster}`
  if (category === 'press') {
    apiQueryUrl += `&categories=${WP_CATEGORY_ID_PRESS}&per_page=30`
  } else if (category === 'blogpost') {
    apiQueryUrl += `&categories=${WP_CATEGORY_ID_BLOGPOST}&per_page=30`
  } else if (category === 'aiGlossary') {
    apiQueryUrl += `&categories=${WP_CATEGORY_ID_AI_GLOSSARY}&per_page=30`
  }
  if (search) {
    apiQueryUrl += `&search=${search}`
  }

  return execWPQuery(apiQueryUrl)
}

export function getWpCategories () {
  const cacheBuster = Date.now()
  let apiQueryUrl = WP_API_URL + `categories/?${cacheBuster}&per_page=100`
  return execWPQuery(apiQueryUrl)
}

export function massageWpPageData (pageData) {
  // clean up wp data - drop all the fields no being used
  let massagedWpPageData = camelizeKeys(
    dropFields(pageData, WHITELIST_PAGE_FIELDS)
  )
  return massagedWpPageData
}

// this data is now cached on S3, pushed there during build (from wp api)
// in local dev, this data can be updated via running `node bin/updateWPPageData.js`
export function getWpPageData (wpPageId) {
  return window
    .fetch(SITE_URL + `static/wp-data/pages/${wpPageId}.json`)
    .then(res => {
      if (res.status !== 200) {
        throw new Error('got bad response from WP api: ' + res.statusText)
      }
      return res.json()
    })
}

export function getNormalisedPathname (link) {
  let pagePathname = parseUrl(link).pathname
  if (!/^\//.test(pagePathname)) {
    pagePathname = '/' + pagePathname // IE doesn't return leading / for pathname
  }
  return pagePathname
}

// used so we can rely on properties being available (in the same format) in components,
// even for pages that aren't connected to WP
export function generatePageDataFromPathname (pathname) {
  let slug
  if (pathname === '/') {
    slug = 'homepage'
  } else {
    const parts = pathname
      // remove parts of the url that wordpress doesn't know about
      .replace(/\/case-studies\//, '')
      .replace(/\/blog\//, '')
      .replace(/\/resources\/glossaries\//, '')
      .replace(/\/labs\//, '')
      .replace(/\/resources\/(white-papers\/)?/, '')
      // remove leading+trailing slashes
      // (should we stop removing trailing slash here, as we know enforce it in rest of codebase?)
      .replace(/^\//, '')
      .replace(/\/$/, '')
      // replaces remaining slashes with - (so seeds/emotional-snapshot becomes seeds-emotional-snapshot, as in WP)
      .replace(/\//, '-')
      .split('/')
    slug = parts[parts.length - 1]
  }

  let title = slug.replace(/-/g, ' ')
  title = title.slice(0, 1).toUpperCase() + title.slice(1)

  const link = 'https://snips.ai' + pathname

  let pageData = {
    title: {
      rendered: 'Snips | ' + title
    },
    slug,
    link,
    acf: {}
  }
  pageData = massageWpPageData(pageData)
  return pageData
}

const ANNOTATED_WORD_GROUP_REGEXP = /(\[[^\]]*])/g
// in WP we support using simple [bracket] annotation in plain text
// in places, to mean either tagged word, or just emphasis.
// this function parses such text and returns an array with annotatedWordGroups ({text, isTag, tagIndex})
export function parseWpTaggedText (annotatedText) {
  let tagIndex = 0
  return (
    annotatedText
      // split into array, with the [annoted] text separate
      .split(ANNOTATED_WORD_GROUP_REGEXP)
      .map((wordGroup, idx) => {
        const [, firstBrace, text, lastBrace] =
          wordGroup.match(/(\[)([^\]]*)(])/) || []
        if (firstBrace && lastBrace) {
          tagIndex++
          return {
            text,
            isTag: true,
            tagIndex
          }
        }
        return { text: wordGroup }
      })
  )
}

// returns smallest image for non IS_WEB,
// otherwise the smallest one that is 1.5x larger than screensize
export function getBestWpImageUrlForFullscreen (wordpressImage) {
  if (!wordpressImage) {
    return
  }
  const { url, sizes } = wordpressImage
  if (!sizes) {
    return url
  }
  if (!IS_WEB) {
    return sizes.thumbnail
  }
  const desiredImageWidth = window.innerWidth * 1.5
  if (desiredImageWidth > sizes.mediumLargeWidth) {
    return sizes.large
  }
  if (desiredImageWidth > sizes.mediumWidth) {
    return sizes.mediumLarge
  }
  if (desiredImageWidth > sizes.thumbnailWidth) {
    return sizes.medium
  }
  return sizes.thumbnail
}

// NOTE: currently assumes we only have to types of posts, 'blog' and 'press'
export function categoryLabelFromPost (
  post,
  availableCategories,
  postType = 'blog'
) {
  if (!post) return

  const postTypeId =
    postType === 'blog' ? WP_CATEGORY_ID_BLOGPOST : WP_CATEGORY_ID_PRESS
  const fallbackLabel = postType === 'blog' ? 'News' : 'Publications'
  const categoryId = (post.categories || []).filter(c => c !== postTypeId)[0]
  const category =
    categoryId &&
    availableCategories &&
    availableCategories.find(c => c.id === categoryId)
  return (category && category.name) || fallbackLabel
}
